import {
  CustomFieldAccessScopeEnum,
  CustomFieldEntityEnum,
  CustomFieldPermissionsEnum,
  CustomFieldPermissionsUiEnum,
  CustomFieldTypeEnum,
  ICustomFieldPermissionsConfig,
  ICustomFieldRecordSchema,
  ICustomFieldReqUiSchema,
  ICustomFieldResSchema,
  UserRoleEnum,
} from '@monetize/types/app';
import omit from 'lodash/omit';
import {
  CUSTOM_FIELD_ENTITY_DISPLAY,
  CUSTOM_FIELD_VISIBILITY_ROLES,
} from '../constants/customFields';
import { toDateShort } from './dates';
import { nullifyEmptyStrings } from './misc';

/**
 * Custom field entities the tenant may configure. Usage event custom fields are
 * gated behind the `usageCustomFieldsEnabled` tenant entitlement, so the
 * `USAGE_EVENT` option is hidden unless the tenant is entitled — or an existing
 * usage event field is already being edited, so it stays viewable.
 */
export const getCustomFieldEntityDisplay = ({
  usageCustomFieldsEnabled,
  currentEntity,
}: {
  usageCustomFieldsEnabled: boolean;
  currentEntity?: string;
}): Partial<Record<CustomFieldEntityEnum, string>> => {
  if (
    usageCustomFieldsEnabled ||
    currentEntity === CustomFieldEntityEnum.USAGE_EVENT
  ) {
    return CUSTOM_FIELD_ENTITY_DISPLAY;
  }
  return omit(CUSTOM_FIELD_ENTITY_DISPLAY, [CustomFieldEntityEnum.USAGE_EVENT]);
};

export const getResolvedPermissions = (
  field: ICustomFieldResSchema,
): CustomFieldPermissionsEnum[] =>
  field.runningUserPermissions ?? field.permissions ?? [];

export const getPermissionForUi = (
  permissions?: ICustomFieldResSchema['permissions'],
): CustomFieldPermissionsUiEnum => {
  if (!permissions || permissions.includes(CustomFieldPermissionsEnum.WRITE)) {
    return CustomFieldPermissionsUiEnum.EDITABLE;
  }
  if (permissions.includes(CustomFieldPermissionsEnum.READ)) {
    return CustomFieldPermissionsUiEnum.READ_ONLY;
  }
  return CustomFieldPermissionsUiEnum.HIDDEN;
};

export const getPermissionForApi = (
  permissions?: CustomFieldPermissionsUiEnum,
): CustomFieldPermissionsEnum[] => {
  if (permissions === CustomFieldPermissionsUiEnum.EDITABLE) {
    return [CustomFieldPermissionsEnum.READ, CustomFieldPermissionsEnum.WRITE];
  }
  if (permissions === CustomFieldPermissionsUiEnum.READ_ONLY) {
    return [CustomFieldPermissionsEnum.READ];
  }
  return [];
};

export const getRoleVisibilityForUi = (
  permissions?: ICustomFieldResSchema['permissions'],
): CustomFieldPermissionsUiEnum => {
  if (!permissions?.length) {
    return CustomFieldPermissionsUiEnum.HIDDEN;
  }
  return getPermissionForUi(permissions);
};

/** Sentinel returned by {@link getCustomFieldVisibilityDisplay} when specific roles don't all share the same visibility. */
export const CUSTOM_FIELD_VISIBILITY_VARIES = 'VARIES_BY_ROLE';

/**
 * Single display value for the custom fields list's Visibility column.
 * ALL_ROLES resolves directly to its permission. SPECIFIC_ROLES resolves to
 * the shared permission when every role matches, otherwise
 * {@link CUSTOM_FIELD_VISIBILITY_VARIES}.
 */
export const getCustomFieldVisibilityDisplay = (
  permissionsConfig?: ICustomFieldResSchema['permissionsConfig'],
): CustomFieldPermissionsUiEnum | typeof CUSTOM_FIELD_VISIBILITY_VARIES => {
  if (
    permissionsConfig?.accessScope !== CustomFieldAccessScopeEnum.SPECIFIC_ROLES
  ) {
    return getPermissionForUi(permissionsConfig?.permissions);
  }
  const [firstRoleVisibility, ...restRoleVisibilities] =
    CUSTOM_FIELD_VISIBILITY_ROLES.map((role) =>
      getRoleVisibilityForUi(permissionsConfig.permissionsByRole[role]),
    );
  return restRoleVisibilities.every(
    (visibility) => visibility === firstRoleVisibility,
  )
    ? firstRoleVisibility
    : CUSTOM_FIELD_VISIBILITY_VARIES;
};

export const buildDefaultVisibilityByRole = (
  visibility: CustomFieldPermissionsUiEnum,
): Partial<Record<UserRoleEnum, CustomFieldPermissionsUiEnum>> =>
  CUSTOM_FIELD_VISIBILITY_ROLES.reduce(
    (acc, role) => {
      acc[role] = visibility;
      return acc;
    },
    {} as Partial<Record<UserRoleEnum, CustomFieldPermissionsUiEnum>>,
  );

export const getUiPermissionFields = (
  permissionsConfig?: ICustomFieldResSchema['permissionsConfig'],
) => {
  if (
    permissionsConfig?.accessScope === CustomFieldAccessScopeEnum.SPECIFIC_ROLES
  ) {
    return {
      accessScope: CustomFieldAccessScopeEnum.SPECIFIC_ROLES,
      allRolesVisibility: CustomFieldPermissionsUiEnum.EDITABLE,
      visibilityByRole: CUSTOM_FIELD_VISIBILITY_ROLES.reduce(
        (acc, role) => {
          acc[role] = getRoleVisibilityForUi(
            permissionsConfig.permissionsByRole[role],
          );
          return acc;
        },
        {} as Partial<Record<UserRoleEnum, CustomFieldPermissionsUiEnum>>,
      ),
    };
  }
  return {
    accessScope: CustomFieldAccessScopeEnum.ALL_ROLES,
    allRolesVisibility: getPermissionForUi(permissionsConfig?.permissions),
    visibilityByRole: buildDefaultVisibilityByRole(
      CustomFieldPermissionsUiEnum.EDITABLE,
    ),
  };
};

export const buildPermissionsConfig = (
  data: Pick<
    ICustomFieldReqUiSchema,
    'accessScope' | 'allRolesVisibility' | 'visibilityByRole'
  >,
): ICustomFieldPermissionsConfig => {
  if (data.accessScope === CustomFieldAccessScopeEnum.ALL_ROLES) {
    return {
      accessScope: CustomFieldAccessScopeEnum.ALL_ROLES,
      permissions: getPermissionForApi(data.allRolesVisibility),
    };
  }
  const permissionsByRole = CUSTOM_FIELD_VISIBILITY_ROLES.reduce(
    (acc, role) => {
      const visibility = data.visibilityByRole?.[role];
      if (visibility && visibility !== CustomFieldPermissionsUiEnum.HIDDEN) {
        acc[role] = getPermissionForApi(visibility);
      }
      return acc;
    },
    {} as Partial<Record<UserRoleEnum, CustomFieldPermissionsEnum[]>>,
  );
  return {
    accessScope: CustomFieldAccessScopeEnum.SPECIFIC_ROLES,
    permissionsByRole,
  };
};

export const sortCustomFields = (
  fieldA: ICustomFieldResSchema,
  fieldB: ICustomFieldResSchema,
) => {
  const fieldAWritable = getResolvedPermissions(fieldA).includes(
    CustomFieldPermissionsEnum.WRITE,
  );
  const fieldBWritable = getResolvedPermissions(fieldB).includes(
    CustomFieldPermissionsEnum.WRITE,
  );
  if (fieldAWritable !== fieldBWritable) {
    return fieldAWritable ? -1 : 1;
  }

  return fieldA.displayLabel
    .toLowerCase()
    .localeCompare(fieldB.displayLabel.toLowerCase());
};

export const handleCustomFieldToDisplayBannerFormat = (
  valueFields: ICustomFieldRecordSchema,
  customFields: ICustomFieldResSchema[],
) => {
  /**
   * lets not mutate the array in place
   * beacuse the caller may pass the reference itself
   */
  const customFieldList = [...customFields].sort(sortCustomFields);

  const customFieldToDisplay = customFieldList.map((field) => {
    const fieldKey = field.key;
    const fieldValue = valueFields[fieldKey]?.toString() ?? 'Blank';
    const fieldLabel = field.displayLabel;

    return {
      label: fieldLabel,
      value: fieldValue,
    };
  });

  return customFieldToDisplay;
};

/**
 * Convert form values to request format
 * @param values ICustomFieldRecordSchema
 * @returns ICustomFieldRecordSchema
 */
export const convertFormValuesToReq = (values?: ICustomFieldRecordSchema) => {
  if (typeof values !== 'object') {
    return values || {};
  }

  return nullifyEmptyStrings(values);
};

/**
 * Narrowest a custom field may be squeezed to before the grid drops a column.
 * Below roughly this, labels like `annual_price_adjustment` stop fitting and
 * the inputs become stubs.
 */
const MIN_CUSTOM_FIELD_WIDTH_REM = 11;

/**
 * `grid-template-columns` for a run of custom fields.
 *
 * `fieldColSpan` keeps its meaning — 12/span is still the most columns you get,
 * and at full width the layout is identical to a plain 12-column grid. What
 * changes is the floor: each track also has a minimum, so a form dropped into a
 * narrow card, drawer or popover sheds columns instead of squeezing every field
 * into an unreadable sliver.
 *
 * This keys off the container rather than the viewport, which a media query
 * can't do — the same form renders at full page width and inside a 20rem
 * popover, and only the box it sits in knows which.
 *
 * `auto-fill` rather than `auto-fit` so a lone field keeps its column width
 * instead of stretching across the whole row.
 */
export const getCustomFieldGridTemplateColumns = (
  fieldColSpan: number,
  /** Grid gap in rem, needed to work out what one column is worth */
  gapRem: number,
) => {
  // A span outside 1-12 can only mean full width
  const span =
    Number.isFinite(fieldColSpan) && fieldColSpan > 0
      ? Math.min(12, Math.round(fieldColSpan))
      : 12;
  const columns = Math.floor(12 / span);
  if (columns === 1) {
    return '1fr';
  }
  const gaps = `${(columns - 1) * gapRem}rem`;
  return `repeat(auto-fill, minmax(max(${MIN_CUSTOM_FIELD_WIDTH_REM}rem, (100% - ${gaps}) / ${columns}), 1fr))`;
};

export const getCustomFieldDisplayValue = (
  type: CustomFieldTypeEnum,
  value: unknown,
  fallbackIfBlank: string | number | null = null,
): string | number | null => {
  if (value === null || value === undefined) {
    return fallbackIfBlank;
  }
  switch (type) {
    case CustomFieldTypeEnum.CHECKBOX:
      return value ? 'True' : 'False';
    case CustomFieldTypeEnum.DATE:
      return toDateShort(value as string) || fallbackIfBlank;
    case CustomFieldTypeEnum.MULTI_SELECT:
      return Array.isArray(value) ? value.join(', ') : fallbackIfBlank;
    case CustomFieldTypeEnum.NUMBER:
    case CustomFieldTypeEnum.SINGLE_LINE_TEXT:
    case CustomFieldTypeEnum.DROPDOWN:
      return (value as string) ?? fallbackIfBlank;
  }
};
